import * as React from 'react'
import { useAppForm } from '@/hooks/app-form'
import { useApiMutation, useApiQuery } from '@/api/ApiQueryServiceProvider'
import {
  EditResourceInstanceFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type EditResourceInstanceFormData
} from './schema'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import { useSelectOptionsMap } from '@/hooks/useSelectOptions'
import {
  renderTextControl,
  renderSelectControl,
  renderTextareaControl,
  renderButtonControl,
  type ControlConfig
} from '../../formRenderFunctions'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { buildResourceInstanceUri } from '@/hooks/useRemaUriBuilder'
import { sanitizeResourceInstanceName } from '@/lib/utils'
import { AppLogger } from '@/lib/logger'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Database } from 'lucide-react'

const log = AppLogger.getLogger(import.meta)

export type EditResourceInstanceComposerProps = {
  onSuccess?: () => void
  onError?: (error: Error) => void
  onCancel?: () => void
  initialValues?: Partial<EditResourceInstanceFormData>
  showHeader?: boolean
  submitLabel?: string
}

const validateField = (name: keyof EditResourceInstanceFormData, value: unknown): string | null => {
  const shape = EditResourceInstanceFormSchema.shape[name]
  if (!shape) return null
  const parsed = shape.safeParse(value)
  if (!parsed.success) {
    return parsed.error.issues[0]?.message ?? 'Invalid value'
  }
  return null
}

// Instance names must be lowercase (they form part of the resource instance URI).
const validateLowercaseName = (value: unknown): string | null => {
  if (typeof value === 'string' && value.trim() !== '' && value !== value.toLowerCase()) {
    return 'Name must be in lowercase letters only'
  }
  return null
}

export function EditResourceInstanceComposer(props: EditResourceInstanceComposerProps) {
  const { onSuccess, onError, onCancel, initialValues, showHeader = true, submitLabel } = props

  const controlsByGroup = FormConfigLoader.getControlsByGroup(FORM_CONFIG)
  const groups = FormConfigLoader.getGroups(FORM_CONFIG)

  const isRequiredField = (name: string) => {
    return ['namespace', 'resourceCategory', 'resourceTypeNumber', 'resourceInstanceName', 'resourceInstanceDescription'].includes(name)
  }

  // Only needed for the loading state — the actual options are derived from
  // allResourceTypes and filtered by the selected namespace + category below.
  const { loadingMap } = useSelectOptionsMap({
    resourceTypeNumber: {
      endpoint: 'getResourceTypes',
      dataKey: 'resourceTypes',
      valueField: 'resourceTypeNumber',
      labelField: 'resourceTypeName',
    }
  })

  // Fetch all resource types to resolve parentResourceTypes chain
  const { data: resourceTypesData } = useApiQuery('getResourceTypes', {})
  const allResourceTypes: any[] = resourceTypesData?.content?.resourceTypes ?? []

  // parentInstanceIdMap: { [resourceTypeName]: instanceId }
  // Keyed by type name so values survive type switches and are restored if user goes back
  const [parentInstanceIdMap, setParentInstanceIdMap] = React.useState<Record<string, string>>(() => {
    const map: Record<string, string> = {}
    for (const p of (initialValues?.parentInstancePaths ?? [])) {
      map[p.resourceType] = p.instanceId
    }
    return map
  })

  const upsertResourceInstance = useApiMutation('upsertResourceInstance', 'POST', {
    invalidates: ['getResourceInstances'],
    onSuccess: () => onSuccess?.(),
    onError: (error) => {
      log.error('Failed to update resource instance:', error)
      onError?.(error)
    },
  })

  const form = useAppForm({
    schema: EditResourceInstanceFormSchema,
    defaultValues: {
      instanceNumber: initialValues?.instanceNumber,
      namespace: initialValues?.namespace ?? ('' as any),
      resourceCategory: initialValues?.resourceCategory ?? ('' as any),
      resourceTypeNumber: initialValues?.resourceTypeNumber ?? ('' as any),
      parentInstancePaths: initialValues?.parentInstancePaths ?? [],
      resourceInstanceName: initialValues?.resourceInstanceName ?? '',
      resourceInstanceDescription: initialValues?.resourceInstanceDescription ?? '',
    },
    onSubmit: async ({ value }) => {
      const formValue = value as EditResourceInstanceFormData

      // Derive current parent paths from the selected type's parent chain
      const selectedNum = formValue.resourceTypeNumber ? Number(formValue.resourceTypeNumber) : 0
      const selectedType = allResourceTypes.find((rt: any) => rt.resourceTypeNumber === selectedNum)
      const parentTypeNumbers: number[] = selectedType?.parentResourceTypes ?? []
      const resolvedParentPaths = parentTypeNumbers
        .map((num: number) => allResourceTypes.find((rt: any) => rt.resourceTypeNumber === num))
        .filter(Boolean)
        .map((pt: any) => ({
          resourceType: pt.resourceTypeName as string,
          instanceId: parentInstanceIdMap[pt.resourceTypeName] ?? '',
        }))

      const parsed = EditResourceInstanceFormSchema.safeParse({
        instanceNumber: initialValues?.instanceNumber,
        namespace: formValue.namespace,
        resourceCategory: formValue.resourceCategory,
        resourceTypeNumber: selectedNum,
        parentInstancePaths: resolvedParentPaths,
        resourceInstanceName: formValue.resourceInstanceName?.trim() ?? '',
        resourceInstanceDescription: formValue.resourceInstanceDescription?.trim() ?? '',
      })

      if (!parsed.success) {
        for (const issue of parsed.error.issues) {
          const fieldName = issue.path[0]
          if (typeof fieldName === 'string') {
            form.setFieldMeta(fieldName as any, (meta) => ({
              ...meta,
              errors: [issue.message],
            }))
          }
        }
        return
      }

      // Enforce lowercase instance name before submit.
      const nameCaseError = validateLowercaseName(parsed.data.resourceInstanceName)
      if (nameCaseError) {
        form.setFieldMeta('resourceInstanceName', (meta) => ({ ...meta, isTouched: true, errors: [nameCaseError] }))
        return
      }

      const apiRequest = transformToApiRequest(parsed.data)
      const instanceUri = buildResourceInstanceUri({
        namespace: formValue.namespace,
        tenantId: formValue.namespace === 'cnx' ? 997 : 897,
        category: formValue.resourceCategory,
        parentInstancePaths: resolvedParentPaths,
        resourceType: selectedType?.resourceTypeName ?? '',
        instanceName: formValue.resourceInstanceName?.trim() ?? '',
      })
      await upsertResourceInstance.mutateAsync({ ...apiRequest, instanceUri })
    },
  })

  // Filter the Resource Type options by the selected namespace + category so the
  // dropdown only offers types that match the chosen namespace/category.
  const liveValues = form.state.values as EditResourceInstanceFormData
  const selectedNamespace = liveValues.namespace
  const selectedCategory = liveValues.resourceCategory
  const filteredResourceTypeOptions = allResourceTypes
    .filter(
      (rt: any) =>
        (!selectedNamespace || rt.namespace === selectedNamespace) &&
        (!selectedCategory || rt.resourceTypeCategory === selectedCategory),
    )
    .map((rt: any) => ({ value: String(rt.resourceTypeNumber), label: rt.resourceTypeName }))

  // Derive parent types from the currently selected resource type
  const selectedTypeNumber = Number((form.state.values as EditResourceInstanceFormData).resourceTypeNumber)
  const selectedType = allResourceTypes.find((rt: any) => rt.resourceTypeNumber === selectedTypeNumber)
  const parentTypeNumbers: number[] = selectedType?.parentResourceTypes ?? []
  const parentTypes = parentTypeNumbers
    .map((num: number) => allResourceTypes.find((rt: any) => rt.resourceTypeNumber === num))
    .filter(Boolean) as any[]

  // Live preview of the instance URI built from the current form state, shown
  // under the Instance Name field (same builder used on submit).
  const liveInstanceUri = buildResourceInstanceUri({
    namespace: liveValues.namespace,
    tenantId: liveValues.namespace === 'cnx' ? 997 : 897,
    category: liveValues.resourceCategory,
    parentInstancePaths: parentTypes.map((pt: any) => ({
      resourceType: pt.resourceTypeName as string,
      instanceId: parentInstanceIdMap[pt.resourceTypeName] ?? '',
    })),
    resourceType: selectedType?.resourceTypeName ?? '',
    instanceName: liveValues.resourceInstanceName?.trim() ?? '',
  })

  const renderControl = (control: any) => {
    const isRequired = isRequiredField(control.name)

    if (control.name === 'resourceTypeNumber') {
      return (
        <form.AppField
          key={control.name}
          name={control.name}
          validators={{
            onBlur: ({ value }) => validateField(control.name as keyof EditResourceInstanceFormData, value) || undefined,
          }}
        >
          {(bound) => {
            const config: ControlConfig = {
              name: control.name,
              label: control.label,
              placeholder: control.placeholder,
              description:
                !selectedNamespace || !selectedCategory
                  ? 'Select a namespace and category to see matching resource types'
                  : `${filteredResourceTypeOptions.length} resource type(s) for ${selectedNamespace} / ${selectedCategory}`,
              required: isRequired,
              visibility: control.visibility ?? true,
              disabled: control.disabled ?? false,
              options: filteredResourceTypeOptions,
              isLoading: loadingMap.resourceTypeNumber,
            }

            const { SelectField } = bound
            return (
              <>
                <SelectField
                  label={config.label}
                  required={config.required}
                  placeholder={config.isLoading ? 'Loading...' : config.placeholder || ''}
                  options={config.options || []}
                  description={config.description}
                  disabled={config.disabled}
                />
                {selectedType?.resourceTypeUri && (
                  <div className="mt-2 rounded-md border border-slate-200 bg-muted/40 px-3 py-2">
                    <p className="text-xs font-medium text-muted-foreground">Resource Type URI</p>
                    <p className="mt-0.5 break-all font-mono text-sm">{selectedType.resourceTypeUri}</p>
                  </div>
                )}
              </>
            )
          }}
        </form.AppField>
      )
    }

    return (
      <form.AppField
        key={control.name}
        name={control.name}
        validators={{
          onBlur: ({ value }) =>
            validateField(control.name as keyof EditResourceInstanceFormData, value) || undefined,
        }}
        listeners={
          control.name === 'namespace' || control.name === 'resourceCategory'
            ? {
                // Changing namespace/category re-filters resource types, so clear
                // any previously chosen type that may no longer match.
                onChange: () => form.setFieldValue('resourceTypeNumber', '' as any),
              }
            : control.name === 'resourceInstanceName'
            ? {
                // Auto-correct the instance name as the user types: lowercase, and only
                // lowercase letters, digits and hyphens are kept (everything else stripped).
                onChange: ({ value }) => {
                  const sanitized = sanitizeResourceInstanceName(value)
                  if (sanitized !== value) form.setFieldValue('resourceInstanceName', sanitized as any)
                },
              }
            : undefined
        }
      >
        {(bound) => {
          const config: ControlConfig = {
            name: control.name,
            label: control.label,
            placeholder: control.placeholder,
            description: control.description,
            required: isRequired,
            visibility: control.visibility ?? true,
            disabled: control.disabled ?? false,
          }

          if (control.htmlControl === 'textarea') return renderTextareaControl(config, bound)
          if (control.htmlControl === 'select') {
            return renderSelectControl({
              ...config,
              options: control.selectOptions?.map((opt: any) => ({ value: opt.value, label: opt.label })) || [],
            }, bound)
          }
          if (control.name === 'resourceInstanceName') {
            return (
              <>
                {renderTextControl(config, bound)}
                {liveValues.resourceInstanceName?.trim() && (
                  <div className="mt-2 rounded-md border border-slate-200 bg-muted/40 px-3 py-2">
                    <p className="text-xs font-medium text-muted-foreground">Instance URI</p>
                    <p className="mt-0.5 break-all font-mono text-sm">{liveInstanceUri}</p>
                  </div>
                )}
              </>
            )
          }
          return renderTextControl(config, bound)
        }}
      </form.AppField>
    )
  }

  const renderFormContent = () => {
    return groups.map((group) => {
      const groupControls = controlsByGroup.get(group.formGroupName) || []
      if (groupControls.length === 0) return null
      return (
        <div key={group.formGroupName} className="space-y-4">
          {groupControls.map(renderControl)}
        </div>
      )
    })
  }

  const formBody = (
    <>
        {renderFormContent()}

        {/* Dynamic parentInstancePaths inputs — shown when selected type has parent types */}
        {parentTypes.length > 0 && (
          <div className="space-y-3">
            <div>
              <p className="text-sm font-medium">Parent Instances</p>
              <p className="text-xs text-muted-foreground">
                Specify the instance ID for each required parent type
              </p>
            </div>
            {parentTypes.map((pt: any) => (
              <div key={pt.resourceTypeNumber} className="space-y-1.5">
                <Label className="text-sm">
                  {pt.resourceTypeName}
                  <span className="ml-1 text-base font-bold text-destructive">*</span>
                </Label>
                <Input
                  value={parentInstanceIdMap[pt.resourceTypeName] ?? ''}
                  onChange={(e) =>
                    setParentInstanceIdMap((prev) => ({
                      ...prev,
                      [pt.resourceTypeName]: e.target.value,
                    }))
                  }
                  placeholder={`Instance ID for ${pt.resourceTypeName}`}
                />
              </div>
            ))}
          </div>
        )}

        <div className="pt-4 flex justify-center gap-3">
          {renderButtonControl({
            label: FORM_CONFIG.cancelButton?.label ?? '',
            variant: 'outline',
            onClick: onCancel,
            visibility: !!onCancel,
            type: 'button'
          })}

          {renderButtonControl({
            label: upsertResourceInstance.isPending
              ? (FORM_CONFIG.submitButton?.loadingLabel ?? '')
              : (submitLabel ?? FORM_CONFIG.submitButton?.label ?? ''),
            variant: 'default',
            type: 'submit'
          }, { SubmitButton: form.SubmitButton })}
        </div>
    </>
  )

  return (
    <form.AppForm>
      {showHeader ? (
        <div className="space-y-4">
          <Card className="shadow-sm pb-0 overflow-hidden">
            <CardHeader className="bg-slate-700 text-white [&_p]:text-slate-300 pb-6">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <Database className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">{FORM_CONFIG.formTitle}</CardTitle>
                  <CardDescription>{FORM_CONFIG.formDescription}</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="space-y-6 pt-6">{formBody}</CardContent>
          </Card>
        </div>
      ) : (
        <div className="space-y-6">{formBody}</div>
      )}
    </form.AppForm>
  )
}

export {
  EditResourceInstanceFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type EditResourceInstanceFormData
} from './schema'
